import React, { useEffect, useState } from "react";
import { User, Bell, Shield, Save, Loader2, Mail } from "lucide-react";
import Layout from "../components/Layout";
import { apiRequest } from "../api";

export default function SettingsPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notifications, setNotifications] = useState(true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const loadProfile = async function () {
    try {
      setLoading(true);
      setError("");

      const data = await apiRequest("/users/me");

      const profile = (data && data.user) || data;

      setName((profile && profile.name) || "");
      setEmail((profile && profile.email) || "");

      if (profile && typeof profile.notifications_enabled !== "undefined") {
        setNotifications(Boolean(profile.notifications_enabled));
      }
    } catch (err) {
      setError(err.message || "Unable to load profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(function () {
    loadProfile();
  }, []);

  const validateName = function () {
    const value = name.trim();

    if (!value) {
      return "Name is required.";
    }
    if (value.length < 2) {
      return "Name must contain at least 2 characters.";
    }
    if (value.length > 50) {
      return "Name cannot exceed 50 characters.";
    }
    if (!/^[A-Za-zÀ-ÿ\s'-]+$/.test(value)) {
      return "Name can contain letters, spaces, apostrophes and hyphens only.";
    }
    return "";
  };

  const validateEmail = function () {
    const value = email.trim().toLowerCase();

    if (!value) {
      return "Email is required.";
    }
    if (value.length > 150) {
      return "Email is too long.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Please enter a valid email address.";
    }
    return "";
  };

  const handleSave = async function () {
    setError("");
    setSuccess("");

    const nameError = validateName();
    if (nameError) {
      setError(nameError);
      return;
    }

    const emailError = validateEmail();
    if (emailError) {
      setError(emailError);
      return;
    }

    try {
      setSaving(true);

      const data = await apiRequest("/users/me", {
        method: "PUT",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          notifications_enabled: notifications,
        }),
      });

      const updatedUser = (data && data.user) || data;

      if (updatedUser) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }

      setSuccess("Changes saved successfully.");
    } catch (err) {
      setError(err.message || "Unable to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async function () {
    setError("");
    setSuccess("");

    if (!currentPassword) {
      setError("Current password is required.");
      return;
    }
    if (!newPassword) {
      setError("New password is required.");
      return;
    }
    if (newPassword.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (!/[A-Za-z]/.test(newPassword)) {
      setError("New password must contain at least one letter.");
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setError("New password must contain at least one number.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }
    if (currentPassword === newPassword) {
      setError("New password must be different from the current password.");
      return;
    }

    try {
      setSaving(true);

      const data = await apiRequest("/users/me/password", {
        method: "PUT",
        body: JSON.stringify({
          currentPassword: currentPassword,
          newPassword: newPassword,
        }),
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setSuccess((data && data.message) || "Password changed successfully.");
    } catch (err) {
      setError(err.message || "Unable to change password.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Settings">
        <div className="flex items-center justify-center py-24">
          <div className="flex items-center gap-2 text-sm text-[#7C8AA5]">
            <Loader2 size={18} className="animate-spin" />
            Loading settings...
          </div>
        </div>
      </Layout>
    );
  }

  const initial = name ? name.charAt(0).toUpperCase() : "U";

  return (
    <Layout title="Settings">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* PROFILE HEADER CARD */}
        <div className="rounded-2xl border border-[#1B2033] bg-gradient-to-br from-[#1B2033] to-[#0E1220] p-6 flex items-center gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#5B8DEF] to-[#9067E8] text-2xl font-bold text-white">
            {initial}
          </div>

          <div className="min-w-0">
            <div className="text-base font-bold text-white truncate">
              {name || "User"}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-[#7C8AA5] mt-0.5 truncate">
              <Mail size={12} />
              {email || "No email"}
            </div>
          </div>
        </div>

        {/* GLOBAL MESSAGES */}
        {error ? (
          <div className="rounded-xl bg-[#E5566B]/10 border border-[#E5566B]/20 text-[#E5566B] text-sm px-4 py-3">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="rounded-xl bg-[#3FBF8F]/10 border border-[#3FBF8F]/20 text-[#3FBF8F] text-sm px-4 py-3">
            {success}
          </div>
        ) : null}

        {/* PROFILE */}
        <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#5B8DEF]/15">
              <User size={16} className="text-[#6E93FF]" />
            </div>
            <div>
              <h2 className="font-semibold text-[15px] text-white">Profile</h2>
              <p className="text-xs text-[#7C8AA5]">Update your account information.</p>
            </div>
          </div>

          <div className="mb-4">
            <label className="text-xs text-[#7C8AA5] mb-1.5 block">Name</label>
            <input
              type="text"
              value={name}
              maxLength={50}
              onChange={function (e) {
                setName(e.target.value);
                setError("");
                setSuccess("");
              }}
              placeholder="Your full name"
              className="w-full bg-[#161B2C] border border-[#2A3149] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#5B8DEF] transition"
            />
            <div className="text-[10px] text-[#5B6478] mt-1">
              Letters, spaces, apostrophes and hyphens only.
            </div>
          </div>

          <div>
            <label className="text-xs text-[#7C8AA5] mb-1.5 block">Email</label>
            <input
              type="email"
              value={email}
              maxLength={150}
              onChange={function (e) {
                setEmail(e.target.value);
                setError("");
                setSuccess("");
              }}
              placeholder="your@email.com"
              className="w-full bg-[#161B2C] border border-[#2A3149] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#5B8DEF] transition"
            />
          </div>
        </div>

        {/* NOTIFICATIONS */}
        <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F5B942]/15">
              <Bell size={16} className="text-[#F5B942]" />
            </div>
            <div>
              <h2 className="font-semibold text-[15px] text-white">Notifications</h2>
              <p className="text-xs text-[#7C8AA5]">Manage account notification preferences.</p>
            </div>
          </div>

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-sm text-white">Email notifications</div>
              <div className="text-xs text-[#7C8AA5] mt-1">
                Receive important account updates by email.
              </div>
            </div>

            <button
              type="button"
              onClick={function () {
                setNotifications(function (v) {
                  return !v;
                });
                setError("");
                setSuccess("");
              }}
              className={
                "relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 " +
                (notifications ? "bg-[#5B8DEF]" : "bg-[#2A3149]")
              }
            >
              <span
                className="absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all duration-200"
                style={{ left: notifications ? "22px" : "2px" }}
              />
            </button>
          </label>
        </div>

        {/* SECURITY */}
        <div className="rounded-2xl border border-[#1B2033] bg-[#0E1220] p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#8B5CF6]/15">
              <Shield size={16} className="text-[#B79CFF]" />
            </div>
            <div>
              <h2 className="font-semibold text-[15px] text-white">Security</h2>
              <p className="text-xs text-[#7C8AA5]">Change your account password.</p>
            </div>
          </div>

          <div className="mb-3">
            <label className="text-xs text-[#7C8AA5] mb-1.5 block">Current password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={function (e) {
                setCurrentPassword(e.target.value);
                setError("");
                setSuccess("");
              }}
              autoComplete="current-password"
              placeholder="Current password"
              className="w-full bg-[#161B2C] border border-[#2A3149] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#5B8DEF] transition"
            />
          </div>

          <div className="mb-3">
            <label className="text-xs text-[#7C8AA5] mb-1.5 block">New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={function (e) {
                setNewPassword(e.target.value);
                setError("");
                setSuccess("");
              }}
              autoComplete="new-password"
              placeholder="New password"
              className="w-full bg-[#161B2C] border border-[#2A3149] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#5B8DEF] transition"
            />
            <div className="text-[10px] text-[#5B6478] mt-1">
              Minimum 8 characters with at least one letter and one number.
            </div>
          </div>

          <div className="mb-4">
            <label className="text-xs text-[#7C8AA5] mb-1.5 block">
              Confirm new password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={function (e) {
                setConfirmPassword(e.target.value);
                setError("");
                setSuccess("");
              }}
              autoComplete="new-password"
              placeholder="Confirm new password"
              className="w-full bg-[#161B2C] border border-[#2A3149] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#5B8DEF] transition"
            />
          </div>

          <button
            type="button"
            onClick={handleChangePassword}
            disabled={saving}
            className="flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl border border-[#2A3149] text-[#D8DEEA] hover:bg-[#161B2C] disabled:opacity-50 transition"
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : null}
            Change Password
          </button>
        </div>

        {/* SAVE */}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-[#5B8DEF] to-[#4A7DE0] text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50 transition"
        >
          {saving ? (
            <React.Fragment>
              <Loader2 size={16} className="animate-spin" />
              Saving...
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Save size={16} />
              Save Changes
            </React.Fragment>
          )}
        </button>
      </div>
    </Layout>
  );
}